/**
 * AppPostsPage.tsx
 *
 * Combined "App Posts" screen that merges the former BlogPostsPage and
 * BlogConfigPage into a single tabbed page at /marketing/posts.
 *
 *   - Posts tab    → list/CRUD blog posts (was BlogPostsPage)
 *   - Settings tab → blog config form (was BlogConfigPage)
 *
 * The active tab is reflected in the ?tab= query param so that legacy
 * redirects (/marketing/blog → posts, /marketing/blog-config → settings)
 * land users on the right tab.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Star,
  Image as ImageIcon,
  X,
  Upload,
  Lock,
  Share2,
  Unlink,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { BlogPostPhonePreview } from './BlogPostPhonePreview';
import { BlogPostPreviewModal } from './BlogPostPreviewModal';
import { TiptapEditor } from '@/v5/components/ui/tiptap-editor';
import { Tabs } from '@/components/ui/Tabs';
import { useToast } from '@/hooks/useToast';
import { usePageContext } from '@/hooks/usePageContext';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/apiClient';
import { presignAndPutFile, SIZE_THRESHOLD } from '@/services/uploads.service';

// ─── Posts: Types ────────────────────────────────────────────────────────────

interface BlogPostShare {
  blogPostAccountID: number;
  blogPostID: number;
  accountID: number;
  facilityID: number | null;
  createDate: string;
}

interface BlogPost {
  blogPostID: number;
  ownerAccountID: number;
  ownerFacilityID: number | null;
  slug: string;
  title: string;
  subtitle: string | null;
  body: string;
  heroImageUrl: string | null;
  readingTime: number;
  tags: string[] | null;
  featured: boolean;
  status: 'draft' | 'published';
  publishedAt: string | null;
  createdByStaffID: number | null;
  createdByUserID: number | null;
  active: boolean;
  createDate: string;
  updateDate: string;
  shares?: BlogPostShare[];
}

interface BlogPostsPageResponse {
  data: BlogPost[];
  meta: { page: number; perPage: number; total: number; lastPage: number };
}

interface Filters {
  status: 'draft' | 'published' | '';
  featured: boolean;
  q: string;
  page: number;
}

// ─── Posts: API ──────────────────────────────────────────────────────────────

async function fetchPosts(filters: Filters): Promise<BlogPostsPageResponse> {
  const params: Record<string, any> = { page: filters.page, perPage: 20, allFacilities: 1 };
  if (filters.status) params.status = filters.status;
  if (filters.featured) params.featured = 1;
  if (filters.q.trim()) params.q = filters.q.trim();
  const { data } = await apiClient.get('/blog/posts', { params });
  return { data: data.data, meta: data.meta };
}

async function fetchPost(id: number): Promise<BlogPost> {
  const { data } = await apiClient.get(`/blog/posts/${id}`, { params: { allFacilities: 1 } });
  return data.data;
}

// Override the apiClient's default 'application/json' Content-Type so axios
// lets the browser set 'multipart/form-data; boundary=...' for FormData bodies.
const MULTIPART_CONFIG = {
  headers: { 'Content-Type': undefined as unknown as string },
};

async function createPost(form: FormData): Promise<BlogPost> {
  const { data } = await apiClient.post('/blog/posts', form, MULTIPART_CONFIG);
  return data.data;
}

async function updatePost(id: number, form: FormData): Promise<BlogPost> {
  form.append('_method', 'PUT');
  const { data } = await apiClient.post(`/blog/posts/${id}`, form, MULTIPART_CONFIG);
  return data.data;
}

async function deletePost(id: number): Promise<void> {
  await apiClient.delete(`/blog/posts/${id}`);
}

async function updateShares(
  id: number,
  shares: Array<{ accountID: number; facilityID: number | null }>,
): Promise<BlogPostShare[]> {
  const { data } = await apiClient.put(`/blog/posts/${id}/shares`, { shares });
  return data.data;
}

interface FacilityOption {
  facilityID: number;
  name: string;
  active: boolean;
}

async function fetchFacilities(): Promise<FacilityOption[]> {
  const { data } = await apiClient.get('/facilities');
  const rows: any[] = data.data ?? [];
  return rows
    .map((row) => ({
      facilityID: Number(row.facilityID ?? row.facilityId ?? 0),
      name: String(row.name ?? row.facilityName ?? '').trim() || `Facility ${row.facilityID ?? ''}`,
      active: row.active !== false,
    }))
    .filter((f) => f.facilityID > 0 && f.active);
}

// NOTE: the cross-account "share with other accounts" picker was removed —
// practice posts can only target facilities of the SAME account (the
// Facilities field). Global / cross-account distribution is a Corp-dashboard
// capability, and posts shared in from elsewhere are view-only here.

// ─── Settings: Types & API ───────────────────────────────────────────────────

interface BlogConfig {
  blogConfigID: number;
  ownerAccountID: number;
  blogTitle: string;
  postsPerPage: number;
  active: boolean;
  createDate: string;
  updateDate: string;
  isShared: boolean;
  isOwner: boolean;
}

interface BlogConfigUpdatePayload {
  blogTitle?: string;
  postsPerPage?: number;
}

async function fetchBlogConfig(): Promise<BlogConfig> {
  const { data } = await apiClient.get('/blog/config');
  return data.data;
}

async function saveBlogConfig(payload: BlogConfigUpdatePayload): Promise<BlogConfig> {
  const { data } = await apiClient.put('/blog/config', payload);
  return data.data;
}

async function unlinkBlogConfig(): Promise<BlogConfig> {
  const { data } = await apiClient.delete('/blog/config/link');
  return data.data;
}

// ─── Page ────────────────────────────────────────────────────────────────────

const TAB_IDS = ['posts', 'settings'] as const;
type TabId = (typeof TAB_IDS)[number];

function isTabId(value: string | null | undefined): value is TabId {
  return value === 'posts' || value === 'settings';
}

export default function AppPostsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: TabId = isTabId(tabParam) ? tabParam : 'posts';

  const tabs = [
    { id: 'posts', label: 'Posts', content: <PostsTab /> },
    { id: 'settings', label: 'Settings', content: <SettingsTab /> },
  ];

  return (
    <div className="space-y-5 p-5">
      <div className="flex items-center gap-3">
        <FileText className="h-6 w-6 text-sky-700" />
        <h1 className="text-xl font-semibold text-slate-800">App Posts</h1>
      </div>

      <Tabs
        tabs={tabs}
        defaultTab={initialTab}
        onChange={(id) => {
          const next = new URLSearchParams(searchParams);
          if (id === 'posts') {
            next.delete('tab');
          } else {
            next.set('tab', id);
          }
          setSearchParams(next, { replace: true });
        }}
      />
    </div>
  );
}

// ─── Posts Tab ───────────────────────────────────────────────────────────────

function PostsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const updatePageContext = usePageContext('App Posts');
  const { accountId: currentAccountID } = useAuth();

  const [filters, setFilters] = useState<Filters>({ status: '', featured: false, q: '', page: 1 });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPostID, setEditingPostID] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BlogPost | null>(null);
  // Shared/global posts (owned by another account, e.g. corp) are view-only.
  const [previewPost, setPreviewPost] = useState<BlogPost | null>(null);
  const isOwnPost = (p: BlogPost) => Number(p.ownerAccountID) === Number(currentAccountID);

  const listQuery = useQuery({
    queryKey: ['blog', 'posts', filters],
    queryFn: () => fetchPosts(filters),
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (listQuery.data) {
      updatePageContext({
        dataSummary: `Posts tab — ${listQuery.data.meta.total} post(s) total${
          filters.status ? `, status: ${filters.status}` : ''
        }`,
        filters: [
          filters.status && `status=${filters.status}`,
          filters.featured && 'featured',
          filters.q && `q="${filters.q}"`,
        ].filter(Boolean).join(', ') || 'none',
      });
    }
  }, [listQuery.data, filters, updatePageContext]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog', 'posts'] });
      toast.success('Post deleted');
      setDeleteTarget(null);
    },
    onError: () => {
      toast.error('Failed to delete post');
      setDeleteTarget(null);
    },
  });

  const handleNewPost = () => {
    setEditingPostID(null);
    setEditorOpen(true);
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPostID(post.blogPostID);
    setEditorOpen(true);
  };

  const posts = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={handleNewPost}>
          New Post
        </Button>
      </div>

      <Card>
        <CardBody>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <FormField label="Search" htmlFor="bp-q">
                <Input
                  id="bp-q"
                  placeholder="Search title, subtitle, body…"
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))}
                  fullWidth
                />
              </FormField>
            </div>
            <div className="w-[180px]">
              <FormField label="Status" htmlFor="bp-status">
                <Select
                  id="bp-status"
                  value={filters.status}
                  onChange={(v) => setFilters((f) => ({ ...f, status: (v ?? '') as Filters['status'], page: 1 }))}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'published', label: 'Published' },
                  ]}
                />
              </FormField>
            </div>
            <div className="flex items-center gap-2 h-10">
              <Checkbox
                id="bp-featured"
                checked={filters.featured}
                onChange={(e) => setFilters((f) => ({ ...f, featured: e.target.checked, page: 1 }))}
              />
              <label htmlFor="bp-featured" className="text-sm text-slate-700 select-none">
                Featured only
              </label>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="relative min-h-[200px]">
        {listQuery.isLoading && <SpinnerOverlay label="Loading posts…" />}

        {!listQuery.isLoading && posts.length === 0 && (
          <Card>
            <CardBody>
              <EmptyState
                icon={<FileText className="h-8 w-8 text-slate-400" />}
                title="No posts yet"
                description="Click New Post to create your first entry."
                action={{ label: 'New Post', onClick: handleNewPost, leftIcon: <Plus className="h-4 w-4" /> }}
              />
            </CardBody>
          </Card>
        )}

        {posts.length > 0 && (
          <div className="space-y-3">
            {posts.map((post) => (
              <Card key={post.blogPostID}>
                <CardBody>
                  <div className="flex items-start gap-4">
                    {post.heroImageUrl ? (
                      <img
                        src={post.heroImageUrl}
                        alt=""
                        className="h-20 w-28 object-cover rounded border border-slate-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="h-20 w-28 rounded bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-slate-800 truncate">{post.title}</h3>
                        <Badge variant={post.status === 'published' ? 'success' : 'neutral'}>
                          {post.status}
                        </Badge>
                        {post.featured && (
                          <Badge variant="warning">
                            <Star className="h-3 w-3 inline mr-1" />
                            Featured
                          </Badge>
                        )}
                        {post.ownerFacilityID && (
                          <Badge variant="info">Facility {post.ownerFacilityID}</Badge>
                        )}
                      </div>
                      {post.subtitle && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{post.subtitle}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                        <span className="font-mono">{post.slug}</span>
                        <span>·</span>
                        <span>{post.readingTime} min read</span>
                        {post.publishedAt && (
                          <>
                            <span>·</span>
                            <span>Published {new Date(post.publishedAt).toLocaleDateString()}</span>
                          </>
                        )}
                        {post.tags && post.tags.length > 0 && (
                          <>
                            <span>·</span>
                            <div className="flex items-center gap-1 flex-wrap">
                              {post.tags.slice(0, 4).map((tag) => (
                                <span key={tag} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                  {tag}
                                </span>
                              ))}
                              {post.tags.length > 4 && <span>+{post.tags.length - 4}</span>}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isOwnPost(post) ? (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<Pencil className="h-4 w-4" />}
                            onClick={() => handleEdit(post)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            onClick={() => setDeleteTarget(post)}
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Owned elsewhere (e.g. a corp-made global post) — view only. */}
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                            title="This post was shared with your account by its owner. It can be viewed but not edited."
                          >
                            <Share2 className="h-3 w-3" />
                            Shared · view only
                          </span>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setPreviewPost(post)}
                          >
                            View
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>

      {meta && meta.lastPage > 1 && (
        <Pagination
          total={meta.total}
          page={meta.page}
          pageSize={meta.perPage}
          onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
        />
      )}

      {editorOpen && (
        <BlogPostEditorModal
          postID={editingPostID}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['blog', 'posts'] });
            setEditorOpen(false);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete post?"
        message={
          <span>
            <span className="font-semibold">{deleteTarget?.title}</span> will be removed. This is a soft delete — the data stays in the database but it will no longer appear anywhere.
          </span>
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={async () => {
          if (deleteTarget) await deleteMutation.mutateAsync(deleteTarget.blogPostID);
        }}
        onClose={() => setDeleteTarget(null)}
      />

      {previewPost && (
        <BlogPostPreviewModal
          data={{
            title: previewPost.title,
            subtitle: previewPost.subtitle ?? '',
            body: previewPost.body,
            tags: previewPost.tags ?? [],
            readingTime: previewPost.readingTime,
            heroImageUrl: previewPost.heroImageUrl ?? '',
            heroImageFile: null,
          }}
          onClose={() => setPreviewPost(null)}
        />
      )}
    </div>
  );
}

// ─── Settings Tab ────────────────────────────────────────────────────────────

function SettingsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const updatePageContext = usePageContext('App Posts');

  const [blogTitle, setBlogTitle] = useState('');
  const [postsPerPage, setPostsPerPage] = useState<number>(10);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const configQuery = useQuery({
    queryKey: ['blog', 'config'],
    queryFn: fetchBlogConfig,
  });

  useEffect(() => {
    if (configQuery.data) {
      setBlogTitle(configQuery.data.blogTitle);
      setPostsPerPage(configQuery.data.postsPerPage);
      updatePageContext({
        dataSummary: `Settings tab — "${configQuery.data.blogTitle}" · ${configQuery.data.postsPerPage} per page${
          configQuery.data.isShared ? ' (shared config)' : ''
        }`,
      });
    }
  }, [configQuery.data, updatePageContext]);

  const saveMutation = useMutation({
    mutationFn: saveBlogConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog', 'config'] });
      toast.success('Settings saved');
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error?.code;
      const message = err?.response?.data?.error?.message ?? 'Failed to save settings';
      if (code === 'NOT_CONFIG_OWNER') {
        toast.error('These settings are shared and owned by another account. Unlink first to customize.');
      } else {
        toast.error(message);
      }
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: unlinkBlogConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog', 'config'] });
      toast.success('Unlinked from shared settings. A fresh config has been created for this account.');
      setConfirmUnlink(false);
    },
    onError: () => {
      toast.error('Failed to unlink settings');
      setConfirmUnlink(false);
    },
  });

  const config = configQuery.data;
  const canEdit = !!config?.isOwner;
  const isDirty =
    !!config && (blogTitle !== config.blogTitle || postsPerPage !== config.postsPerPage);

  const handleSave = () => {
    if (!config || !canEdit) return;
    const payload: BlogConfigUpdatePayload = {};
    if (blogTitle !== config.blogTitle) payload.blogTitle = blogTitle.trim();
    if (postsPerPage !== config.postsPerPage) payload.postsPerPage = postsPerPage;
    if (Object.keys(payload).length === 0) return;
    saveMutation.mutate(payload);
  };

  if (configQuery.isLoading) {
    return (
      <div className="relative min-h-[240px]">
        <SpinnerOverlay label="Loading settings…" />
      </div>
    );
  }

  if (configQuery.isError || !config) {
    return (
      <Card className="max-w-[700px]">
        <CardBody>
          <p className="text-sm text-red-600">Failed to load settings. Try refreshing the page.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-5 max-w-[700px]">
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-700">Settings</h2>
            {config.isShared && (
              <Badge variant="info">
                <Share2 className="h-3 w-3 mr-1 inline" />
                Shared
              </Badge>
            )}
            {!canEdit && (
              <Badge variant="neutral">
                <Lock className="h-3 w-3 mr-1 inline" />
                Read-only
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardBody className="space-y-5">
          {!canEdit && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              This account is using settings owned by another account (ID {config.ownerAccountID}). Changes will
              affect every account that shares them, so only the owner can edit. Unlink below to create an independent
              config for this account.
            </div>
          )}

          <FormField label="Title" htmlFor="ap-title" required>
            <Input
              id="ap-title"
              value={blogTitle}
              onChange={(e) => setBlogTitle(e.target.value)}
              placeholder="e.g. Our Clinic Blog"
              disabled={!canEdit}
              maxLength={255}
              fullWidth
            />
          </FormField>

          <FormField
            label="Posts per page"
            htmlFor="ap-posts-per-page"
            helpText="How many posts the public feed shows per page (1-200)."
            required
          >
            <Input
              id="ap-posts-per-page"
              type="number"
              min={1}
              max={200}
              value={postsPerPage}
              onChange={(e) => setPostsPerPage(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
              disabled={!canEdit}
              fullWidth
            />
          </FormField>

          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-slate-500">
              Config ID: <span className="font-mono">{config.blogConfigID}</span>
            </div>
            <div className="flex items-center gap-2">
              {config.isShared && (
                <Button
                  variant="secondary"
                  leftIcon={<Unlink className="h-4 w-4" />}
                  onClick={() => setConfirmUnlink(true)}
                  disabled={unlinkMutation.isPending}
                >
                  Unlink from shared
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saveMutation.isPending}
                disabled={!canEdit || !isDirty || saveMutation.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <ConfirmDialog
        isOpen={confirmUnlink}
        title="Unlink from shared settings?"
        message="This account will get a brand new, independent config. The shared config will no longer affect this account. Posts are not affected."
        confirmLabel="Unlink"
        variant="danger"
        onConfirm={async () => {
          await unlinkMutation.mutateAsync();
        }}
        onClose={() => setConfirmUnlink(false)}
      />
    </div>
  );
}

// ─── Editor Modal ─────────────────────────────────────────────────────────────

interface EditorProps {
  postID: number | null;
  onClose: () => void;
  onSaved: () => void;
}

interface EditorFormState {
  title: string;
  slug: string;
  subtitle: string;
  body: string;
  readingTime: number;
  tags: string[];
  featured: boolean;
  status: 'draft' | 'published';
  facilityIDs: number[];
  heroImageUrl: string;
  heroImageFile: File | null;
}

function emptyState(): EditorFormState {
  return {
    title: '',
    slug: '',
    subtitle: '',
    body: '',
    readingTime: 0,
    tags: [],
    featured: false,
    status: 'draft',
    facilityIDs: [],
    heroImageUrl: '',
    heroImageFile: null,
  };
}

function BlogPostEditorModal({ postID, onClose, onSaved }: EditorProps) {
  const toast = useToast();
  const { accountId: currentAccountID } = useAuth();
  const isEdit = postID !== null;

  const [form, setForm] = useState<EditorFormState>(emptyState());
  const [tagDraft, setTagDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const postQuery = useQuery({
    queryKey: ['blog', 'post', postID],
    queryFn: () => fetchPost(postID as number),
    enabled: isEdit,
  });

  const facilitiesQuery = useQuery({
    queryKey: ['facilities', 'list'],
    queryFn: fetchFacilities,
    staleTime: 5 * 60 * 1000,
  });

  const facilityOptions = useMemo(() => {
    const opts = (facilitiesQuery.data ?? []).map((f) => ({ id: f.facilityID, label: f.name }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [facilitiesQuery.data]);

  useEffect(() => {
    if (postQuery.data) {
      const p = postQuery.data;
      const allShares = p.shares ?? [];
      const ownerAccountShares = allShares.filter((s) => s.accountID === p.ownerAccountID);
      const hasAccountWide = ownerAccountShares.some((s) => s.facilityID === null);
      const facilityIDs = hasAccountWide
        ? []
        : ownerAccountShares
            .filter((s) => s.facilityID !== null)
            .map((s) => s.facilityID as number);
      setForm({
        title: p.title,
        slug: p.slug,
        subtitle: p.subtitle ?? '',
        body: p.body,
        readingTime: p.readingTime,
        tags: p.tags ?? [],
        featured: p.featured,
        status: p.status,
        facilityIDs,
        heroImageUrl: p.heroImageUrl ?? '',
        heroImageFile: null,
      });
    }
  }, [postQuery.data]);

  const buildSharesPayload = () => {
    const ownerFacilityID = form.facilityIDs[0] ?? null;
    const extraOwnerFacilityShares =
      currentAccountID && form.facilityIDs.length > 1
        ? form.facilityIDs.slice(1).map((fid) => ({ accountID: currentAccountID, facilityID: fid }))
        : [];
    return {
      ownerFacilityID,
      // Facilities of the owner account only — cross-account sharing is corp-only.
      shareWith: extraOwnerFacilityShares,
    };
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { ownerFacilityID, shareWith } = buildSharesPayload();
      const fd = new FormData();
      fd.append('title', form.title);
      if (form.slug) fd.append('slug', form.slug);
      if (form.subtitle) fd.append('subtitle', form.subtitle);
      fd.append('body', form.body);
      fd.append('readingTime', String(form.readingTime));
      fd.append('tags', JSON.stringify(form.tags));
      fd.append('featured', form.featured ? '1' : '0');
      fd.append('status', form.status);
      if (ownerFacilityID !== null) fd.append('ownerFacilityID', String(ownerFacilityID));
      if (form.heroImageFile) {
        // Large hero images bypass Lambda's 6 MB payload cap via presigned S3.
        if (form.heroImageFile.size > SIZE_THRESHOLD) {
          const s3Key = await presignAndPutFile(form.heroImageFile);
          fd.append('s3Key', s3Key);
        } else {
          fd.append('heroImageFile', form.heroImageFile);
        }
      } else if (form.heroImageUrl) {
        fd.append('heroImageUrl', form.heroImageUrl);
      }
      if (!isEdit) {
        fd.append('shareWith', JSON.stringify(shareWith));
      }
      return isEdit ? updatePost(postID as number, fd) : createPost(fd);
    },
    onSuccess: async (saved) => {
      if (isEdit) {
        try {
          const { shareWith } = buildSharesPayload();
          await updateShares(saved.blogPostID, shareWith);
        } catch {
          toast.warning('Post saved, but sharing update failed — retry from the editor.');
          onSaved();
          return;
        }
      }
      toast.success(isEdit ? 'Post updated' : 'Post created');
      onSaved();
    },
    onError: (err: any) => {
      const message = err?.response?.data?.error?.message ?? 'Failed to save post';
      toast.error(message);
    },
  });

  const addTag = () => {
    const t = tagDraft.trim().toLowerCase();
    if (!t) return;
    if (form.tags.includes(t)) {
      setTagDraft('');
      return;
    }
    setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagDraft('');
  };

  const removeTag = (t: string) => {
    setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setForm((f) => ({ ...f, heroImageFile: file }));
  };

  const heroPreview = form.heroImageFile
    ? URL.createObjectURL(form.heroImageFile)
    : form.heroImageUrl;

  const autoReadingTime = useMemo(() => {
    const words = form.body.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  }, [form.body]);

  const canSave = form.title.trim().length > 0 && form.body.trim().length > 0 && !saveMutation.isPending;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Post' : 'New Post'}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saveMutation.isPending}
            disabled={!canSave}
            onClick={() => saveMutation.mutate()}
          >
            {isEdit ? 'Save Changes' : 'Create Post'}
          </Button>
        </>
      }
    >
      {isEdit && postQuery.isLoading ? (
        <div className="relative min-h-[200px]">
          <SpinnerOverlay label="Loading post…" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-4">
          <FormField label="Title" htmlFor="bp-edit-title" required>
            <Input
              id="bp-edit-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={255}
              fullWidth
              placeholder="A catchy post title"
            />
          </FormField>

          <FormField
            label="Slug"
            htmlFor="bp-edit-slug"
            helpText="Optional — lowercase letters, numbers, and hyphens. Auto-derived from title if blank."
          >
            <Input
              id="bp-edit-slug"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              maxLength={255}
              fullWidth
              placeholder="my-post-title"
            />
          </FormField>

          <FormField label="Subtitle" htmlFor="bp-edit-subtitle">
            <Input
              id="bp-edit-subtitle"
              value={form.subtitle}
              onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
              maxLength={500}
              fullWidth
            />
          </FormField>

          <FormField label="Hero image" helpText="PNG, JPG, WebP, GIF — up to 5MB. Or paste a URL below.">
            <div className="space-y-2">
              {heroPreview && (
                <div className="relative inline-block">
                  <img src={heroPreview} alt="" className="h-32 rounded border border-slate-200" />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 rounded-full bg-white border border-slate-300 p-0.5 shadow"
                    onClick={() => setForm((f) => ({ ...f, heroImageFile: null, heroImageUrl: '' }))}
                    aria-label="Remove image"
                  >
                    <X className="h-3 w-3 text-slate-600" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={onFilePicked}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Upload className="h-4 w-4" />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {form.heroImageFile ? 'Replace image' : 'Upload image'}
                </Button>
                <span className="text-xs text-slate-500">or</span>
                <Input
                  placeholder="https://…"
                  value={form.heroImageFile ? '' : form.heroImageUrl}
                  onChange={(e) => setForm((f) => ({ ...f, heroImageUrl: e.target.value, heroImageFile: null }))}
                  disabled={!!form.heroImageFile}
                  fullWidth
                />
              </div>
            </div>
          </FormField>

          <FormField label="Body" required>
            <TiptapEditor
              key={isEdit ? `bp-body-${postID}` : 'bp-body-new'}
              content={isEdit ? (postQuery.data?.body ?? '') : ''}
              onChange={(html) => setForm((f) => ({ ...f, body: html }))}
              placeholder="Write your post — use the toolbar for bold, italic, links, images, lists, headings…"
              className="min-h-[340px]"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Reading time (minutes)"
              htmlFor="bp-edit-reading"
              helpText={`Auto-estimate: ${autoReadingTime} min`}
            >
              <div className="flex items-center gap-2">
                <Input
                  id="bp-edit-reading"
                  type="number"
                  min={0}
                  value={form.readingTime}
                  onChange={(e) => setForm((f) => ({ ...f, readingTime: Math.max(0, Number(e.target.value) || 0) }))}
                  fullWidth
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setForm((f) => ({ ...f, readingTime: autoReadingTime }))}
                >
                  Auto
                </Button>
              </div>
            </FormField>

            <FormField
              label="Visible at facilities"
              helpText={
                facilitiesQuery.isLoading
                  ? 'Loading facilities…'
                  : form.facilityIDs.length === 0
                    ? 'No facilities selected — post is visible account-wide.'
                    : `Visible at ${form.facilityIDs.length} facility(ies) in this account.`
              }
            >
              <MultiSelectDropdown
                options={facilityOptions}
                selected={new Set(form.facilityIDs)}
                onToggle={(id) =>
                  setForm((f) => ({
                    ...f,
                    facilityIDs: f.facilityIDs.includes(id)
                      ? f.facilityIDs.filter((x) => x !== id)
                      : [...f.facilityIDs, id],
                  }))
                }
                onSelectAll={(visibleIds) =>
                  setForm((f) => ({
                    ...f,
                    // While the dropdown is being searched it hands up only the
                    // matching ids, which merge into the current picks.
                    facilityIDs: visibleIds
                      ? [...new Set([...f.facilityIDs, ...visibleIds])]
                      : facilityOptions.map((o) => o.id),
                  }))
                }
                onClearAll={() => setForm((f) => ({ ...f, facilityIDs: [] }))}
                placeholder="All facilities (account-wide)"
                searchable
              />
            </FormField>
          </div>

          <FormField label="Tags" htmlFor="bp-edit-tag">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  id="bp-edit-tag"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Type a tag and press Enter"
                  maxLength={50}
                  fullWidth
                />
                <Button variant="secondary" size="sm" onClick={addTag}>
                  Add
                </Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {form.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => removeTag(t)}
                        className="hover:text-red-600"
                        aria-label={`Remove ${t}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status" htmlFor="bp-edit-status">
              <Select
                id="bp-edit-status"
                value={form.status}
                onChange={(v) => setForm((f) => ({ ...f, status: (v ?? 'draft') as 'draft' | 'published' }))}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'published', label: 'Published' },
                ]}
              />
            </FormField>
            <div className="flex items-end gap-2 pb-2">
              <Checkbox
                id="bp-edit-featured"
                checked={form.featured}
                onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
              />
              <label htmlFor="bp-edit-featured" className="text-sm text-slate-700 select-none">
                Feature this post
              </label>
            </div>
          </div>

          {/* Cross-account sharing removed: practice posts target facilities of
              the SAME account only (the "Facilities" field above). Cross-account
              / global distribution is a Corp-dashboard capability. */}
          </div>{/* end form column */}

          <div className="hidden lg:block">
            <div className="sticky top-0">
              <BlogPostPhonePreview
                title={form.title}
                subtitle={form.subtitle}
                body={form.body}
                heroImageUrl={heroPreview || null}
                tags={form.tags}
                readingTime={form.readingTime}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
